import "./style.css";
import "@fortawesome/fontawesome-free/js/fontawesome";
import "@fortawesome/fontawesome-free/js/solid";
import "@fortawesome/fontawesome-free/js/brands";

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import * as dat from "dat.gui";
import Stats from "stats.js";

import * as UTILS from "./ts/utils";
import * as THREE_UTILS from "./ts/three/utils";
import { getSea } from "./ts/sea/sea";
import { getPointer } from "./ts/pointer";
import { addDayNightToggle } from "./ts/day_night";
import { Subscribable } from "./ts/subscribable";
import anime from "animejs";

const gui = new dat.GUI();
gui.hide();

const stats = new Stats();
stats.showPanel(0);
const child = document.body.appendChild(stats.dom);
const statsDebug = {
  toggleStats: () => {
    child.style.display = child.style.display == "none" ? "block" : "none";
  },
};
statsDebug.toggleStats();
gui.add(statsDebug, "toggleStats");

const scene = new THREE.Scene();

const windowSize = UTILS.getWindowSize();

const performanceParameters = {
  maxFrameRate: 60,
};

const camera = THREE_UTILS.getPerspectiveCamera(50, 1, 25, windowSize);

const cameraParameters = {
  radius: 10,
  period: 120,
  depth: {
    mean: 3,
    amplitude: 2,
  },
  parallax: {
    horizontal: 1.5,
    vertical: 1.0,
    smoothing: 0.5,
  },
};

const pointer = getPointer(windowSize);

const rotateCamera = (t: THREE_UTILS.Time) => {
  const radius = cameraParameters.radius;
  const peroid = cameraParameters.period;
  const angle = (2 * Math.PI * t.elapsed) / peroid;
  camera.position.x = radius * Math.sin(angle);
  camera.position.y =
    -cameraParameters.depth.mean +
    cameraParameters.depth.amplitude * Math.sin(angle);
  camera.position.z = radius * Math.cos(angle);
  pointer.update(t.delta, cameraParameters.parallax.smoothing);
  camera.position.x +=
    cameraParameters.parallax.horizontal * pointer.position.x * Math.cos(angle);
  camera.position.z -=
    cameraParameters.parallax.horizontal * pointer.position.x * Math.sin(angle);
  camera.position.y += cameraParameters.parallax.vertical * pointer.position.y;
  camera.lookAt(0, -cameraParameters.depth.mean, 0);
};

const cameraGui = gui.addFolder("camera");
cameraGui.add(cameraParameters, "radius").min(0).max(20);
cameraGui.add(cameraParameters, "period").min(0).max(240);
cameraGui.add(cameraParameters.depth, "mean").min(0).max(5);
cameraGui.add(cameraParameters.depth, "amplitude").min(0).max(5);
const parallaxGui = cameraGui.addFolder("parallax");
parallaxGui.add(cameraParameters.parallax, "horizontal").min(0).max(5);
parallaxGui.add(cameraParameters.parallax, "vertical").min(0).max(5);
parallaxGui.add(cameraParameters.parallax, "smoothing").min(0).max(2);

const cameraHelper = THREE_UTILS.getFixedCameraHelper(camera);
THREE_UTILS.addVisibilityToggle(gui, cameraHelper, scene, "cameraHelper");

const renderer = THREE_UTILS.getRenderer(windowSize);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enabled = false;
gui
  .add(controls, "enabled")
  .name("camera")
  .onChange(() => {
    if (!controls.enabled) controls.reset();
  });
controls.target.set(0, camera.position.y, 0);
controls.saveState();
controls.update();

const axesHelper = new THREE.AxesHelper();
THREE_UTILS.addVisibilityToggle(gui, axesHelper, scene, "axesHelper");

const animationLoop = THREE_UTILS.getAnimationLoop(
  () => stats.begin(),
  () => stats.end(),
  performanceParameters
);

animationLoop.time.subscribeOnChange(rotateCamera);

// Readout of what each frame costs. The values are those of the previous
// frame, since the render call is subscribed last.
const frame = {
  fps: 0,
  drawCalls: 0,
  triangles: 0,
  pixelRatio: renderer.getPixelRatio(),
};
const performanceGui = gui.addFolder("performance");
performanceGui.add(frame, "fps").listen();
performanceGui.add(frame, "drawCalls").listen();
performanceGui.add(frame, "triangles").listen();
performanceGui.add(frame, "pixelRatio").listen();
performanceGui
  .add(performanceParameters, "maxFrameRate")
  .min(15)
  .max(120)
  .step(1);
animationLoop.time.subscribeOnChange((t) => {
  frame.fps = Math.round(1 / t.delta);
  frame.drawCalls = renderer.info.render.calls;
  frame.triangles = renderer.info.render.triangles;
});

const isLightMode = UTILS.isLightMode();
const isDay = new Subscribable<number>(isLightMode.value ? 1 : 0);
isLightMode.subscribeOnFinishChange((d) => {
  anime({
    targets: isDay,
    value: d ? 1 : 0,
    duration: 2e3,
    easing: "linear",
    update: () => isDay.callSubscribers(),
  });
});

let dayColor = new THREE.Color("#7696ff");
let nightColor = new THREE.Color("#061222");

let getColor = (d: number) =>
  new THREE.Color().lerpColors(nightColor, dayColor, d);

const parameters = {
  color: new Subscribable(getColor(isDay.value)),
  visibility: { min: new Subscribable(5.0), max: new Subscribable(20.0) },
  depth: new Subscribable(8),
  width: camera.far + 5,
  height: camera.far + 5,
};
isDay.subscribeOnChange((d) => {
  parameters.color.value = getColor(d);
  parameters.color.callSubscribers();
});

const seaGui = gui.addFolder("sea");
const sea = getSea(parameters, renderer, seaGui, animationLoop.time, isDay);
scene.add(sea);
THREE_UTILS.addVisibilityToggle(seaGui, sea, scene, "visible");

animationLoop.time.subscribeOnChange(() => renderer.render(scene, camera));

animationLoop.loop();

addDayNightToggle(isLightMode);
